/**
 * V2 sheet base behaviour (AB#522).
 *
 * Deliberately small. Horizon's <theme-drawer> already provides open(), close(),
 * toggle(), focus trapping via the native <dialog>, scroll lock and focus
 * restore. This file adds only the two things it does not:
 *
 *   1. Declarative triggers, so a sheet can be opened from markup with no
 *      per-sheet script: [data-mlr-sheet-open="<id>"] and [data-mlr-sheet-close].
 *   2. Drag-to-dismiss on the grabber, as a convenience ON TOP OF the close
 *      button. The button always works, so a pointer-only gesture is never the
 *      sole way out. Dismiss needs a deliberate drag, not a flick, or the sheet
 *      closes while someone is scrolling its body.
 *
 * Event delegation on the document, so sheets rendered later by the Section
 * Rendering API work without re-binding. That matters: quick add re-renders
 * sections on every cart change.
 */

/** Minimum downward travel, in px, before a drag counts as a dismissal. */
const DISMISS_DISTANCE = 80;
/** Or a fast flick: px per ms downward. */
const DISMISS_VELOCITY = 0.5;

/** @param {Element | null} el */
function sheetFor(el) {
  return el ? el.closest('theme-drawer') : null;
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-mlr-sheet-open]');
  if (opener) {
    const id = opener.getAttribute('data-mlr-sheet-open');
    const sheet = id ? document.getElementById(id) : null;
    // `open` comes from Horizon's component. If the element is not upgraded yet
    // the click is a no-op rather than an exception, and the next click works.
    if (sheet && typeof (/** @type {any} */ (sheet).open) === 'function') {
      event.preventDefault();
      /** @type {any} */ (sheet).open();
    }
    return;
  }

  const closer = target.closest('[data-mlr-sheet-close]');
  if (closer) {
    const sheet = sheetFor(closer);
    if (sheet && typeof (/** @type {any} */ (sheet).close) === 'function') {
      event.preventDefault();
      /** @type {any} */ (sheet).close();
    }
  }
});

/* ---- drag to dismiss ---------------------------------------------------- */

/** @type {{ sheet: Element, dialog: HTMLElement, startY: number, startedAt: number } | null} */
let drag = null;

document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const grabber = target ? target.closest('.mlr-sheet__grabber') : null;
    if (!grabber) return;

    const sheet = sheetFor(grabber);
    const dialog = sheet ? sheet.querySelector('dialog') : null;
    if (!sheet || !(dialog instanceof HTMLElement)) return;

    // Only on viewports where the sheet IS a bottom sheet. Above 750px it is a
    // centred panel and dragging it down means nothing, which is also why the
    // grabber is display:none there.
    if (window.matchMedia('(min-width: 750px)').matches) return;

    drag = { sheet, dialog, startY: event.clientY, startedAt: performance.now() };
    grabber.setPointerCapture?.(event.pointerId);
  },
  { passive: true }
);

document.addEventListener(
  'pointermove',
  (event) => {
    if (!drag) return;
    const dy = event.clientY - drag.startY;
    // Downward only. Dragging up must not detach the sheet from the bottom edge.
    const offset = Math.max(0, dy);
    drag.dialog.style.transform = `translateY(${offset}px)`;
    drag.dialog.style.transition = 'none';
  },
  { passive: true }
);

function endDrag(event) {
  if (!drag) return;
  const { dialog, sheet, startY, startedAt } = drag;
  const dy = (event.clientY ?? startY) - startY;
  const velocity = dy / Math.max(1, performance.now() - startedAt);

  dialog.style.transform = '';
  dialog.style.transition = '';
  drag = null;

  if (dy > DISMISS_DISTANCE || (dy > 16 && velocity > DISMISS_VELOCITY)) {
    if (typeof (/** @type {any} */ (sheet).close) === 'function') {
      /** @type {any} */ (sheet).close();
    }
  }
}

document.addEventListener('pointerup', endDrag, { passive: true });
document.addEventListener('pointercancel', endDrag, { passive: true });

/* ---- the menu sheet replaces Horizon's drawer on phone ------------------- */

/**
 * Horizon's phone menu is a <details class="menu-drawer-container"> opened by its <summary>.
 * Rather than remove that markup, which the desktop nav and Horizon's own JS still reference,
 * the summary's click is intercepted below the menu-sheet breakpoint and the V2 sheet opens
 * instead. preventDefault stops the <details> from toggling, so the two never both open.
 *
 * Above the breakpoint nothing is intercepted and Horizon behaves exactly as it does today.
 *
 * Delegated from the document so a header re-rendered by the Section Rendering API keeps
 * working with no re-binding.
 */
const MENU_SHEET_MAX = 900;

document.addEventListener(
  'click',
  (event) => {
    if (window.innerWidth >= MENU_SHEET_MAX) return;

    const target = event.target instanceof Element ? event.target : null;
    const summary = target?.closest('summary');
    if (!summary) return;

    // Only the top-level menu trigger, not the submenu disclosures inside the drawer.
    const label = (summary.getAttribute('aria-label') || '').trim().toLowerCase();
    if (label !== 'menu') return;

    const sheet = document.getElementById('mlr-menu-sheet');
    if (!sheet || typeof (/** @type {any} */ (sheet).open) !== 'function') return;

    event.preventDefault();
    event.stopPropagation();

    // If Horizon's drawer is already open, close it first so focus is not left inside a
    // hidden subtree.
    const openDrawer = summary.closest('details[open]');
    if (openDrawer) openDrawer.open = false;

    // Horizon's own click handling adds `menu-open` to the drawer container. Because the
    // <details> never opens here, nothing ever removes it again, and the overlay rule in
    // mlr-tab-bar.css keys on `.menu-drawer-container.menu-open`: the tab bar stayed hidden
    // for the rest of the session after one tap on Menu.
    //
    // Cleared now and again on the next frame, because Horizon's listener may run after this
    // one even from the capture phase.
    const clearMenuOpen = () => {
      for (const el of document.querySelectorAll('.menu-drawer-container.menu-open')) {
        el.classList.remove('menu-open');
      }
    };
    clearMenuOpen();
    requestAnimationFrame(clearMenuOpen);

    /** @type {any} */ (sheet).open();
  },
  // Capture, so this runs before Horizon's own summary handling.
  true
);

/* ---- click outside, at widths where there is no backdrop ----------------- */

/**
 * Close an open sheet when the pointer lands outside it (AB#554).
 *
 * WHY THIS IS NEEDED ONLY AT DESKTOP. Below Horizon's 900px modal breakpoint the dialog is a
 * MODAL dialog, so it has a `::backdrop` and a click outside the panel already closes it. Above
 * it the dialog is non-modal: measured, `dialog.matches(':modal')` is true at 390 and 768 and
 * false at 1280. A non-modal dialog has no backdrop at all, so a click beside the cart drawer
 * landed on the page and nothing happened, while the same gesture closed it on a phone.
 *
 * THE CLOSE GOES THROUGH THE OWNER. `theme-drawer.close()`, never `dialog.close()`, for the same
 * reason the cart's open path does: the component tracks its own open state in an attribute, and
 * anything that moves the dialog behind its back leaves it unable to close what it did not open.
 * That was AB#549.
 *
 * WHAT IT MUST NOT DO:
 *
 *   - close on the click that just opened it. An opener's click reaches the document in the same
 *     bubble phase as this listener, so a sheet would open and shut in one gesture. Openers are
 *     skipped explicitly. (The cart trigger never gets here anyway: mlr-cart-drawer.js
 *     intercepts it in the capture phase and stops propagation.)
 *   - close on a click inside the panel, including on a control that happens to be removed by
 *     its own handler, which is why the test is `dialog.contains(target)` against the element
 *     the event started on.
 *   - touch a modal dialog. The backdrop owns that case, and closing it here as well would mean
 *     two things closing one dialog.
 */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // The gesture that opens a sheet is not the gesture that closes it.
  if (
    target.closest(
      '[data-mlr-sheet-open], [data-testid="cart-drawer-trigger"], [aria-controls="cart-drawer"], [data-mlr-cart-open]'
    )
  ) {
    return;
  }

  for (const host of document.querySelectorAll('theme-drawer')) {
    const dialog = host.querySelector('dialog');
    if (!dialog || !dialog.open) continue;

    // A modal dialog already closes on its backdrop. Leave it alone.
    if (typeof dialog.matches === 'function' && dialog.matches(':modal')) continue;

    if (dialog.contains(target)) continue;

    if (typeof (/** @type {any} */ (host).close) === 'function') {
      /** @type {any} */ (host).close();
    }
  }
});
